import { useState, useEffect } from "react";
import { fetchFollowUpSequence, saveFollowUpSequence } from "../../api/contacts";
import toast from "react-hot-toast";
import { FiClock, FiPlus, FiTrash2, FiSave, FiCheckCircle } from "react-icons/fi";

export default function FollowUpSequenceCard({ token, templates }) {
  const [enabled, setEnabled] = useState(false);
  const [steps, setSteps] = useState([
    { step_number: 1, wait_days: 3, template_id: "" }
  ]);
  const [saving, setSaving] = useState(false);
  const [savedSuccess, setSavedSuccess] = useState(false);

  // Load existing sequence settings
  useEffect(() => {
    if (!token?.access) return;
    fetchFollowUpSequence(token.access)
      .then((data) => {
        if (data) {
          setEnabled(data.is_active || false);
          if (Array.isArray(data.steps) && data.steps.length > 0) {
            setSteps(
              data.steps.map((s, idx) => ({
                step_number: idx + 1,
                wait_days: s.wait_days || 3,
                template_id: s.template_id ? String(s.template_id) : "",
              }))
            );
          }
        }
      })
      .catch((err) => console.error("Failed to load follow-up sequence:", err));
  }, [token]);

  const handleAddStep = () => {
    if (steps.length >= 3) {
      toast.error("Maximum 3 follow-up steps recommended for best deliverability.");
      return;
    }
    setSteps([
      ...steps,
      { step_number: steps.length + 1, wait_days: 5, template_id: "" }
    ]);
  };

  const handleRemoveStep = (index) => {
    const updated = steps.filter((_, idx) => idx !== index).map((s, idx) => ({
      ...s,
      step_number: idx + 1
    }));
    setSteps(updated);
  };

  const handleStepChange = (index, field, value) => {
    const updated = [...steps];
    updated[index][field] = value;
    setSteps(updated);
  };

  const handleSave = async () => {
    if (enabled) {
      const invalid = steps.find((s) => !s.template_id);
      if (invalid) {
        toast.error("Please select a template for all active follow-up steps.");
        return;
      }
    }

    setSaving(true);
    try {
      await saveFollowUpSequence(
        token.access,
        enabled,
        steps.map((s, idx) => ({
          step_number: idx + 1,
          wait_days: Number(s.wait_days) || 3,
          template_id: Number(s.template_id) || null
        }))
      );
      toast.success("Follow-up sequence saved!");
      setSavedSuccess(true);
      setTimeout(() => setSavedSuccess(false), 3000);
    } catch (err) {
      toast.error(err.message || "Failed to save sequence");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm border mb-6 space-y-4">
      {/* Header & Toggle */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-gray-100">
        <div>
          <label className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <FiClock className="text-blue-600" /> 3. Automatic Follow-Up Emails (Optional)
          </label>
          <p className="text-xs text-gray-500 mt-0.5">
            Automatically reach back out to prospects who haven't replied to your initial email.
          </p>
        </div>

        {/* Toggle Switch */}
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          <span className="ml-2.5 text-xs font-bold text-gray-700 uppercase">
            {enabled ? "Follow-Ups Enabled" : "Disabled"}
          </span>
        </label>
      </div>

      {/* Helpful Plain English Hint Banner */}
      <div className="bg-amber-50/70 border border-amber-200 rounded-lg p-3 text-xs text-amber-900 flex items-start gap-2">
        <span className="text-base shrink-0">💡</span>
        <div className="space-y-0.5 leading-relaxed">
          <strong className="block text-amber-950">How Automatic Follow-Ups Work:</strong>
          <p>
            If a contact does <strong>not reply</strong> to your first email, the system waits your specified number of days and sends the follow-up template.
            If they reply or unsubscribe, follow-ups are automatically stopped!
          </p>
        </div>
      </div>

      {/* Follow-Up Steps Builder (Visible when enabled) */}
      {enabled && (
        <div className="space-y-3 pt-2">
          {steps.map((step, idx) => (
            <div
              key={idx}
              className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-gray-50 p-3.5 rounded-lg border border-gray-200"
            >
              <div className="flex items-center gap-2 text-xs font-semibold text-gray-700 shrink-0">
                <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-800 flex items-center justify-center font-bold">
                  {idx + 1}
                </span>
                <span>Follow-Up Step {idx + 1}:</span>
              </div>

              <div className="flex flex-1 flex-col sm:flex-row items-center gap-3">
                <div className="flex items-center gap-1.5 text-xs text-gray-600">
                  <span>Wait</span>
                  <input
                    type="number"
                    min="1"
                    max="30"
                    value={step.wait_days}
                    onChange={(e) => handleStepChange(idx, "wait_days", e.target.value)}
                    className="w-16 border rounded px-2 py-1 text-center bg-white text-xs font-bold"
                  />
                  <span>days after previous email</span>
                </div>

                <select
                  value={step.template_id}
                  onChange={(e) => handleStepChange(idx, "template_id", e.target.value)}
                  className="flex-1 border p-1.5 rounded text-xs bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="">-- Select Follow-Up Template --</option>
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </div>

              {steps.length > 1 && (
                <button
                  type="button"
                  onClick={() => handleRemoveStep(idx)}
                  className="text-gray-400 hover:text-red-600 p-1 transition self-end sm:self-center"
                  title="Remove step"
                >
                  <FiTrash2 size={16} />
                </button>
              )}
            </div>
          ))}

          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2">
            <button
              type="button"
              onClick={handleAddStep}
              className="text-xs text-blue-600 font-semibold hover:underline flex items-center gap-1"
            >
              <FiPlus size={14} /> Add Another Follow-Up Step
            </button>

            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-4 py-2 rounded flex items-center gap-1.5 transition disabled:opacity-50"
            >
              {savedSuccess ? <FiCheckCircle size={14} /> : <FiSave size={14} />}
              {saving ? "Saving..." : savedSuccess ? "Saved!" : "Save Follow-Up Sequence"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
